// src/pages/AccountAddresses.jsx
// v3 — "Add new address" changed from a full-width outline button to a
// dashed-bordered "+" card sitting in a proper grid alongside the address
// cards, matching the Amazon reference layout. Grid auto-collapses to a
// single column on mobile (minmax 260px won't fit 2-up on phone screens),
// so no separate mobile/desktop logic needed. All logic unchanged from v2.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BottomNav from '../components/BottomNav';
import { useStore } from '../context/StoreContext';

const LABEL_OPTIONS = ['Home', 'Office', 'Other'];

export default function AccountAddresses() {
  const { user, savedAddresses, saveAddress, deleteAddress, defaultAddressId, setDefaultAddress } = useStore();
  const navigate = useNavigate();
  const [addingAddress, setAddingAddress] = useState(false);
  const blankAddrForm = { name: '', phone: '', flat: '', street: '', landmark: '', city: '', state: '', pin: '' };
  const [addrForm, setAddrForm] = useState(blankAddrForm);
  const [labelChoice, setLabelChoice] = useState('Home');
  const [customLabel, setCustomLabel] = useState('');
  const [addrError, setAddrError] = useState('');

  if (!user) return (
    <div className="app-shell">
      <Navbar />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 20px', textAlign: 'center' }}>
        <i className="ti ti-user" style={{ fontSize: 52, color: 'var(--muted)', marginBottom: 16 }}></i>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Not signed in</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>Sign in to view your saved addresses</div>
        <button className="btn-primary" style={{ width: 180 }} onClick={() => navigate('/login')}>Sign in</button>
      </div>
      <BottomNav />
    </div>
  );

  function handleStartAdd() {
    setAddrForm(blankAddrForm);
    setLabelChoice('Home');
    setCustomLabel('');
    setAddrError('');
    setAddingAddress(true);
  }
  function handleStartEdit(addr) {
    setAddrForm({ ...addr });
    if (LABEL_OPTIONS.includes(addr.label)) {
      setLabelChoice(addr.label);
      setCustomLabel('');
    } else {
      setLabelChoice('Other');
      setCustomLabel(addr.label || '');
    }
    setAddrError('');
    setAddingAddress(true);
  }
  function handleSaveAddressForm() {
    setAddrError('');
    if (!addrForm.name.trim() || !addrForm.phone.trim() || !addrForm.flat.trim() ||
        !addrForm.street.trim() || !addrForm.landmark.trim() || !addrForm.city.trim() ||
        !addrForm.state.trim() || !addrForm.pin.trim()) {
      setAddrError('Please fill in all fields, including landmark');
      return;
    }
    if (labelChoice === 'Other' && !customLabel.trim()) {
      setAddrError('Please enter a label for this address');
      return;
    }
    const finalLabel = labelChoice === 'Other' ? customLabel.trim() : labelChoice;
    saveAddress({ ...addrForm, label: finalLabel });
    setAddrForm(blankAddrForm);
    setAddingAddress(false);
  }

  return (
    <div className="app-shell">
      <nav style={{ background: '#0D1117', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={() => navigate('/account')}
          style={{ background: 'none', border: 'none', color: 'var(--off-white)', fontSize: 18, cursor: 'pointer', padding: 0, display: 'flex' }}
          aria-label="Back to account"
        >
          <i className="ti ti-arrow-left"></i>
        </button>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--off-white)', letterSpacing: '0.06em' }}>Saved addresses</span>
      </nav>
      <div className="page-body" style={{ paddingBottom: 100 }}>
        {addingAddress && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {addrForm.id ? 'Edit address' : 'New address'}
            </div>
            {addrError && (
              <div style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 12, padding: '10px 14px', borderRadius: 6, marginBottom: 12 }}>
                {addrError}
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Address type</label>
              <select
                value={labelChoice}
                onChange={e => setLabelChoice(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--navy)', background: '#fff', fontFamily: 'var(--font)', outline: 'none' }}
              >
                <option value="Home">Home</option>
                <option value="Office">Office</option>
                <option value="Other">Other (custom label)</option>
              </select>
              {labelChoice === 'Other' && (
                <input
                  placeholder="e.g. Mom's place, Office 2, Ravi - Chennai"
                  value={customLabel}
                  onChange={e => setCustomLabel(e.target.value)}
                  style={{ width: '100%', marginTop: 8, padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--navy)', background: '#fff', fontFamily: 'var(--font)', outline: 'none' }}
                />
              )}
            </div>
            {[
              { key: 'name', ph: 'Full name' },
              { key: 'phone', ph: 'Mobile number' },
              { key: 'flat', ph: 'Flat / House no., Building' },
              { key: 'street', ph: 'Street / Area' },
              { key: 'landmark', ph: 'Landmark (required)' },
              { key: 'city', ph: 'City' },
              { key: 'state', ph: 'State' },
              { key: 'pin', ph: 'Pincode' },
            ].map(f => (
              <input
                key={f.key}
                placeholder={f.ph}
                value={addrForm[f.key]}
                onChange={e => setAddrForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                style={{ width: '100%', padding: '11px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--navy)', background: '#fff', fontFamily: 'var(--font)', outline: 'none', marginBottom: 8 }}
              />
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn-outline" style={{ flex: 1 }} onClick={() => { setAddingAddress(false); setAddrError(''); setAddrForm(blankAddrForm); }}>
                Cancel
              </button>
              <button className="btn-primary" style={{ flex: 1 }} onClick={handleSaveAddressForm}>
                Save address
              </button>
            </div>
          </div>
        )}
        {!addingAddress && (
          <div className="addr-grid">
            <div className="addr-add-card" onClick={handleStartAdd}>
              <i className="ti ti-plus" style={{ fontSize: 28, color: 'var(--muted)', marginBottom: 8 }}></i>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted)' }}>Add address</span>
            </div>
            {savedAddresses.map(addr => (
              <div key={addr.id} className="card addr-card" style={{ border: defaultAddressId === addr.id ? '1.5px solid var(--gold)' : undefined }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{addr.label || 'Address'}</span>
                    {defaultAddressId === addr.id && (
                      <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--gold)', color: '#fff', padding: '2px 7px', borderRadius: 10 }}>DEFAULT</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => handleStartEdit(addr)} style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                      <i className="ti ti-edit"></i>
                    </button>
                    <button onClick={() => deleteAddress(addr.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                      <i className="ti ti-trash"></i>
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 500, marginBottom: 2 }}>{addr.name}</div>
                <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                  {addr.flat}{addr.street ? `, ${addr.street}` : ''}<br />
                  Near {addr.landmark}<br />
                  {addr.city}, {addr.state} - {addr.pin}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, marginBottom: 8 }}>{addr.phone}</div>
                {defaultAddressId !== addr.id && (
                  <button
                    onClick={() => setDefaultAddress(addr.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)', padding: 0, fontWeight: 500 }}
                  >
                    Set as default
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <BottomNav />
      <style>{`
        .addr-grid { display:grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap:12px; }
        .addr-add-card { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:150px; border:1.5px dashed rgba(27,42,74,0.25); border-radius:10px; cursor:pointer; background:#fff; }
        .addr-card { margin-bottom:0; }
      `}</style>
    </div>
  );
}